import { useSelector } from 'react-redux';
import { useDocumentation } from '../../useDocumentation';
import { selectAccountUsers } from '../../../../reducers/accountUsersReducer';
import { selectEnvironments } from '../../../../reducers/global/globalReducer';
import {
  selectParsedFields,
  selectRelatedObjectsByObjectName,
} from '../../../../reducers/global/parserReducer';
import { ConfigurationItemMap } from '../../configuration-item-tables/shared/helpers';
import { ruleByObjectName } from '../../../parser/parserUtils';
import { checkIfActive, createConfigurationItemFromAutomation } from '../../helpers';
import { ConfigurationItem } from '../../../parser/ParserTypes';
import { GlobalData } from '../../../../reducers/global/globalReducerTypes';
import pick from 'lodash/pick';
import { DeployStatusForTable } from '../../../../types/enums/DeployStatusForTable';
import { ConfigurationType } from '../types';
import { MASTER_KEY } from '../../../pages/funnel-map-flow/dialogs/import-funnel/utils';

interface UseGetObjectDependenciesProps {
  crmOrgId: string;
  objectName: string;
  isSupportedType: boolean;
  onlyActive: boolean;
}

export const useGetObjectDependencies = ({
  crmOrgId,
  objectName,
  isSupportedType,
  onlyActive,
}: UseGetObjectDependenciesProps) => {
  const { configurationItems, parsedRecordTypes } = useDocumentation(crmOrgId);
  const users = useSelector(selectAccountUsers);
  const globalEnvs = useSelector(selectEnvironments);
  const fields = useSelector(selectParsedFields(crmOrgId));
  const relatedObjects = useSelector(selectRelatedObjectsByObjectName(crmOrgId))?.[
    objectName ?? ''
  ];

  if (!isSupportedType) {
    return {};
  }

  const attributions = {} as ConfigurationItemMap;

  attributions[ConfigurationType.fields] = fields?.[objectName] ?? [];
  attributions[ConfigurationType.relatedObjects] = relatedObjects ?? [];

  configurationItems?.forEach((rule) => {
    if (ruleByObjectName(objectName)(rule)) {
      const type = rule.parentType as keyof ConfigurationItemMap;

      if (!attributions[type]) {
        attributions[type] = [];
      }

      if (checkIfActive(onlyActive, rule)) {
        attributions[type].push(rule as ConfigurationItem);
      }
    }
  });

  parsedRecordTypes?.forEach((recordType) => {
    if (recordType.objectApiNames[0] === objectName && recordType.name !== MASTER_KEY) {
      const type = ConfigurationType.recordTypes;

      if (!attributions[type]) {
        attributions[type] = [];
      }

      if (checkIfActive(onlyActive, recordType)) {
        attributions[type].push(recordType);
      }
    }
  });

  //adding sweep elements
  const sweepElementsAttributions = {} as ConfigurationItemMap;
  const elements = globalEnvs?.[crmOrgId]?.data;
  const automationElements: Partial<GlobalData> = pick(
    elements ?? {},
    'automations',
    'alerts',
    'scheduledAssignments',
    'assignments',
    'dedup',
    'matching',
  );

  Object.entries(automationElements ?? {})?.forEach(([key, values]) => {
    sweepElementsAttributions[key as keyof ConfigurationItemMap] = [];
    (values as AutomationStructureNew[]).forEach((automation) => {
      if (
        ![DeployStatusForTable.Pending, DeployStatusForTable.Deployed].includes(
          automation.status,
        ) ||
        automation.objectName !== objectName
      ) {
        return;
      }

      if (automation.objectName === objectName) {
        const configurationAutomation = createConfigurationItemFromAutomation(automation, users);

        if (configurationAutomation) {
          sweepElementsAttributions[key as keyof ConfigurationItemMap].push(
            configurationAutomation,
          );
        }
      }
    });
  });

  return { attributions, sweepElementsAttributions };
};
